import {Injectable, OnDestroy} from '@angular/core';
import {captureException} from "@sentry/angular-ivy";
import {BehaviorSubject, Subject, timer} from "rxjs";
import {map, takeUntil, takeWhile} from "rxjs/operators";

@Injectable({
  providedIn: 'root'
})
export class MediaService implements OnDestroy {
  #stream?: MediaStream | null;
  #recorder?: MediaRecorder;
  private isListening$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private micAvailable?: boolean = undefined;
  private timer?: number;
  private timerSeconds?: number;
  private changes: BehaviorSubject<void> = new BehaviorSubject<void>(undefined);
  private chunks: Blob[] = [];
  private recordingAvailable$: Subject<Blob[]> = new Subject<Blob[]>();
  private onDestroy$: Subject<void> = new Subject<void>();


  constructor() {
    this.initializeMicButton().then();
  }

  //region getters
  public get Timer(): number {
    return this.timer as number;
  }

  public get RecordingAvailable(): Subject<Blob[]> {
    return this.recordingAvailable$;
  }

  public get IsMicAvailable(): boolean {
    return this.micAvailable ?? false;
  }

  public get IsMicListening(): boolean {
    return this.isListening$.value;
  }

  public get Changes(): BehaviorSubject<void> {
    return this.changes;
  }

  //endregion

  //region public methods

  configureMicListener(timerSeconds: number): void {

    if (timerSeconds == null)
      throw new Error('timerSeconds cannot be null');

    this.timerSeconds = timerSeconds;

    this.isListening$.pipe(takeUntil(this.onDestroy$))
      .subscribe((value) => {
        if (value) {
          this.timer = this.timerSeconds;
          this.Changes.next();
          timer(0, 1000)
            .pipe(
              takeWhile(() => this.isListening$.value),
              map(x => this.timer as number - 1),
            )
            .subscribe((x) => {
              this.timer = x;

              if (this.timer === 0) {
                this.stopRecording();
              }

              this.Changes.next();
            });
        } else
          this.timer = this.timerSeconds;
      });
  }

  async startRecording(): Promise<void> {
    await this.startListening();

    const options: MediaRecorderOptions = {mimeType: 'audio/webm'};
    this.#recorder = new MediaRecorder(this.#stream as MediaStream, options);

    this.#recorder.ondataavailable = (e) => {
      this.chunks.push(e.data);
      console.info("mic data available");
    }

    this.#recorder.onstop = (e) => {
      console.log("recording stopped");
      this.recordingAvailable$.next(this.chunks);
      this.chunks = [];
    }

    this.#recorder.start();
  }

  async stopRecording(): Promise<void> {
    this.#recorder?.stop();
    this.stopListening();
  }

  async openMediaDevices(constraints: MediaStreamConstraints): Promise<MediaStream | null> {
    try {
      return await navigator.mediaDevices.getUserMedia(constraints);
    } catch (e) {
      switch ((e as DOMException).name) {
        case 'PermissionDeniedError':
        case 'NotAllowedError': {
          alert('You must allow access to your microphone to use this feature');
        }
          break;
        case 'ConstraintNotSatisfiedError': {
          alert('Neither your browser or device support audio recording');
        }
          break;
        default: {
          console.log(e);
          captureException(e);
        }
      }
    }
    return null;
  }

  async reset() {
    await this.stopRecording();
    this.stopListening();
    this.chunks = [];
    this.timer = this.timerSeconds;
  }

  async hasMediaDevices(type: 'audioinput' | 'videoinput' | 'audiooutput'): Promise<boolean> {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.some(device => device.kind === type);
  }

  //endregion

  //region private methods

  private async startListening() {
    this.#stream = await this.openMediaDevices({
      audio: {
        noiseSuppression: true,
        echoCancellation: true
      }, video: false, preferCurrentTab: true
    });

    if (this.#stream) {
      this.isListening$.next(true);
      this.#stream.getTracks().forEach(track => {
        console.log(track)
      });
    }
  }

  private stopListening() {
    this.isListening$.next(false);
    this.#stream?.getTracks().forEach(track => track.stop());
  }

  private async initializeMicButton() {
    try {
      this.micAvailable = await this.hasMediaDevices('audioinput');
    } catch (e) {
      console.log(e);
      captureException(e);
    }
  }

  //endregion

  //region
  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  //endregion
}
